// Local storage utilities for curve sets
import { Curve, CurveSet, CurveSetCurveRef } from '../types';
import { syncInflationConfig } from '../inflation-curves';
import { getSavedCurves, saveCurve } from './curves';

const CURVE_SETS_KEY = 'quantra_curve_sets';

function generateCurveSetRefId(): string {
  return `csref_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

function normalizeCurve(curve: any, fallbackCurrency: string): Curve {
  return {
    ...curve,
    role: curve.role || 'discount',
    currency: curve.currency || fallbackCurrency,
    inflation_curve: curve.inflation_curve
      ? syncInflationConfig(curve.inflation_curve, curve.currency || fallbackCurrency)
      : undefined,
  };
}

function ensureStandaloneCurve(curve: any, fallbackCurrency: string): Curve {
  const normalized = normalizeCurve(curve, fallbackCurrency);
  const existing = getSavedCurves().find(saved => saved.id === normalized.id);
  if (existing) return existing;
  return saveCurve(normalized);
}

function normalizeCurveRefs(raw: any, fallbackCurrency: string): { curve_refs: CurveSetCurveRef[]; migrated: boolean } {
  if (Array.isArray(raw?.curve_refs)) {
    return {
      curve_refs: raw.curve_refs
        .filter((ref: any) => ref?.curve_id)
        .map((ref: any) => ({
          id: ref.id || generateCurveSetRefId(),
          curve_id: ref.curve_id,
          role: ref.role || 'discount',
          ...(ref.label ? { label: ref.label } : {}),
        })),
      migrated: false,
    };
  }

  if (Array.isArray(raw?.curves)) {
    return {
      curve_refs: raw.curves
        .filter((curve: any) => curve?.id)
        .map((curve: any) => {
          const standalone = ensureStandaloneCurve(curve, fallbackCurrency);
          return {
            id: generateCurveSetRefId(),
            curve_id: standalone.id,
            role: standalone.role || 'discount',
            label: standalone.name,
          };
        }),
      migrated: true,
    };
  }

  return { curve_refs: [], migrated: false };
}

function normalizeCurveSet(raw: any): { set: CurveSet; migrated: boolean } {
  const fallbackCurrency = raw?.currency || 'EUR';
  const { curve_refs, migrated } = normalizeCurveRefs(raw, fallbackCurrency);
  return {
    set: {
      ...raw,
      curve_refs,
      credit_curve_ids: Array.isArray(raw?.credit_curve_ids) ? raw.credit_curve_ids.filter(Boolean) : [],
      quote_ids: Array.isArray(raw?.quote_ids) ? raw.quote_ids : [],
      curves: undefined,
    },
    migrated,
  };
}

export function generateCurveSetId(): string {
  return `cset_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

export function getSavedCurveSets(): CurveSet[] {
  try {
    const data = localStorage.getItem(CURVE_SETS_KEY);
    if (!data) return [];
    const rawSets = JSON.parse(data);
    if (!Array.isArray(rawSets)) return [];
    let didMigrate = false;
    const sets = rawSets.map((raw: any) => {
      const normalized = normalizeCurveSet(raw);
      if (normalized.migrated) didMigrate = true;
      return normalized.set;
    });
    if (didMigrate) {
      localStorage.setItem(CURVE_SETS_KEY, JSON.stringify(sets));
    }
    return sets;
  } catch {
    return [];
  }
}

export function saveCurveSet(cs: CurveSet): CurveSet {
  const sets = getSavedCurveSets();
  const idx = sets.findIndex(s => s.id === cs.id);
  const now = new Date().toISOString();
  const updated = {
    ...cs,
    curve_refs: Array.isArray(cs.curve_refs)
      ? cs.curve_refs
          .filter(ref => ref?.curve_id)
          .map(ref => ({
            id: ref.id || generateCurveSetRefId(),
            curve_id: ref.curve_id,
            role: ref.role || 'discount',
            ...(ref.label ? { label: ref.label } : {}),
          }))
      : [],
    credit_curve_ids: Array.isArray(cs.credit_curve_ids) ? cs.credit_curve_ids.filter(Boolean) : [],
    quote_ids: Array.isArray(cs.quote_ids) ? cs.quote_ids : [],
    curves: undefined,
    updatedAt: now,
    createdAt: cs.createdAt || now,
  };
  if (idx >= 0) sets[idx] = updated; else sets.push(updated);
  localStorage.setItem(CURVE_SETS_KEY, JSON.stringify(sets));
  return updated;
}

export function deleteCurveSet(id: string): void {
  const sets = getSavedCurveSets().filter(s => s.id !== id);
  localStorage.setItem(CURVE_SETS_KEY, JSON.stringify(sets));
}

export function getCurveSetById(id: string): CurveSet | null {
  return getSavedCurveSets().find(s => s.id === id) || null;
}

export interface ResolvedCurveSetRef extends CurveSetCurveRef {
  curve: Curve | null;
}

export function resolveCurveSetRefs(cs: CurveSet): ResolvedCurveSetRef[] {
  const savedCurves = getSavedCurves();
  return (cs.curve_refs || []).map(ref => ({
    ...ref,
    curve: savedCurves.find(curve => curve.id === ref.curve_id) || null,
  }));
}

export function resolveCurveSetCurves(cs: CurveSet): Curve[] {
  return resolveCurveSetRefs(cs)
    .map(ref => ref.curve)
    .filter((curve): curve is Curve => curve !== null);
}